import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff, AlertCircle } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { supabase, safeSupabaseOperation } from '../../lib/supabase';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offlineMode, setOfflineMode] = useState(false);

  useEffect(() => {
    document.title = 'Admin Login | Proxy Emobility';
    checkSupabaseConnection();
  }, []);

  const checkSupabaseConnection = async () => {
    if (!supabase) {
      setOfflineMode(true);
      return;
    }

    const { data, error, isOffline } = await safeSupabaseOperation(async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data;
    });

    if (isOffline) {
      setOfflineMode(true);
      return;
    }

    // If we have a session, redirect to dashboard
    if (data?.session) {
      navigate('/admin/dashboard');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      if (offlineMode || !supabase) {
        // Demo mode credentials
        if (credentials.email === 'admin@example.com' && credentials.password === 'password') {
          // Store demo session in localStorage
          localStorage.setItem('demoSession', JSON.stringify({
            user: { email: credentials.email },
            expires_at: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
          }));
          toast.success('Connexion réussie (Mode démo)');
          navigate('/admin/dashboard');
        } else {
          setError('Identifiants invalides. En mode démo, utilisez admin@example.com / password');
          toast.error('Identifiants invalides pour le mode démo');
        }
        return;
      }
      
      // Normal Supabase login using safe operation
      const { data, error, isOffline } = await safeSupabaseOperation(async () => {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password
        });
        if (error) throw error;
        return data;
      });

      if (isOffline) {
        // Switch to offline mode if network error
        setOfflineMode(true);
        setError('Problème de connexion. Basculement en mode démo. Utilisez admin@example.com / password');
        toast.error('Connexion impossible. Mode démo activé.');
        return;
      }

      if (error) {
        // Handle authentication errors
        setError(error.message);
        toast.error(error.message);
        return;
      }
      
      toast.success('Connexion réussie');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      
      // Fallback to demo mode on any unexpected error
      setOfflineMode(true);
      setError('Erreur de connexion. Basculement en mode démo. Utilisez admin@example.com / password');
      toast.error('Erreur de connexion. Mode démo activé.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <Toaster position="top-right" />
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img src="/proxy-logo.png" alt="Proxy Emobility" className="h-16 w-auto" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Espace Administration
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Connectez-vous pour gérer votre site
        </p>
        {offlineMode && (
          <div className="mt-2 text-center">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-yellow-100 text-yellow-800">
              Mode Démo
            </span>
          </div>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-xl sm:px-10 border border-gray-100">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-start">
              <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          
          {offlineMode && (
            <div className="mb-4 p-3 bg-yellow-50 text-yellow-700 rounded-lg flex items-start">
              <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Mode Démo Actif</p>
                <p className="text-sm mt-1">La connexion à la base de données n'est pas disponible.</p>
                <p className="text-sm">Utilisez les identifiants suivants :</p>
                <p className="text-sm font-mono bg-yellow-100 px-2 py-1 rounded mt-1">
                  Email: admin@example.com<br />
                  Mot de passe: password
                </p>
              </div>
            </div>
          )}
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Adresse email
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail size={18} className="text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder={offlineMode ? "admin@example.com" : "votre@email.com"}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={18} className="text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder={offlineMode ? "password" : "••••••••"}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white\" xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24">
                      <circle className="opacity-25\" cx="12\" cy="12\" r="10\" stroke="currentColor\" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Connexion en cours...
                  </>
                ) : (
                  offlineMode ? 'Se connecter (Mode Démo)' : 'Se connecter'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;